import { useEffect, useMemo, useRef, useState } from 'react';
import JSZip from 'jszip';
import { CheckCheck, ChevronLeft, ChevronRight, Download, ImagePlus, Layers3, LoaderCircle, Map, Maximize2, Plus, Search, Trash2, Upload, WandSparkles, X } from 'lucide-react';
import { deletePhoto, getManifest, saveManifest, uploadPhoto, photoUrl } from './api';
import { canvasToBlob, canvasToPresentationJpeg, downloadBlob, loadImage, prepareCreative, readImageFile, recommendedExportLongEdge, renderMockup } from './image';
import { MappingEditor } from './components/MappingEditor';
import { PhotoCard } from './components/PhotoCard';
import type { GeneratedResult, LibraryPhoto, Manifest, ScreenMapping } from './types';

type Mode = 'sales' | 'admin';
type Notice = { type: 'success' | 'error' | 'info'; text: string } | null;

const emptyManifest: Manifest = { version: 1, updatedAt: new Date(0).toISOString(), photos: [] };

function slugify(value: string) {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50);
}

function screenExceedsCreative(screen: ScreenMapping, creative: HTMLCanvasElement) {
  const distance = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y);
  const width = Math.max(distance(screen.corners[0], screen.corners[1]), distance(screen.corners[3], screen.corners[2]));
  const height = Math.max(distance(screen.corners[0], screen.corners[3]), distance(screen.corners[1], screen.corners[2]));
  return width > creative.width * 1.05 || height > creative.height * 1.05;
}

function App() {
  const [mode, setMode] = useState<Mode>('sales');
  const [manifest, setManifest] = useState<Manifest>(emptyManifest);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<Notice>(null);
  const [creativeFile, setCreativeFile] = useState<File | null>(null);
  const [creativeUrl, setCreativeUrl] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<GeneratedResult[]>([]);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [mappingPhoto, setMappingPhoto] = useState<LibraryPhoto | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [zipBusy, setZipBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);
  const creativeInput = useRef<HTMLInputElement>(null);
  const objectUrls = useRef<Set<string>>(new Set());
  const libraryPhotos = useMemo(() => manifest.photos.filter((photo) => photo.mediaType !== 'video'), [manifest]);
  const mappedPhotos = useMemo(() => libraryPhotos.filter((photo) => photo.screens.length), [libraryPhotos]);
  const filteredPhotos = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? mappedPhotos.filter((photo) => `${photo.name} ${photo.file}`.toLowerCase().includes(query)) : mappedPhotos;
  }, [mappedPhotos, search]);
  const allVisibleSelected = filteredPhotos.length > 0 && filteredPhotos.every((photo) => selected.has(photo.id));

  function toggleVisiblePhotos() {
    setSelected((current) => {
      const next = new Set(current);
      const shouldClear = filteredPhotos.length > 0 && filteredPhotos.every((photo) => next.has(photo.id));
      filteredPhotos.forEach((photo) => shouldClear ? next.delete(photo.id) : next.add(photo.id));
      return next;
    });
  }

  useEffect(() => {
    getManifest().then(setManifest).catch((error: Error) => setNotice({ type: 'error', text: error.message })).finally(() => setLoading(false));
  }, []);

  useEffect(() => () => {
    objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrls.current.clear();
  }, []);

  function revoke(url: string | null) {
    if (!url) return;
    URL.revokeObjectURL(url);
    objectUrls.current.delete(url);
  }

  async function pickCreative(file?: File) {
    if (!file) return;
    if (!file.type.startsWith('image/')) return setNotice({ type: 'error', text: 'Choose a JPG, PNG, or WebP image.' });
    const url = URL.createObjectURL(file);
    let image: HTMLImageElement;
    try {
      image = await loadImage(url);
    } catch (error) {
      URL.revokeObjectURL(url);
      return setNotice({ type: 'error', text: error instanceof Error ? error.message : 'The creative could not be loaded.' });
    }
    if (image.naturalHeight <= image.naturalWidth) {
      URL.revokeObjectURL(url);
      return setNotice({ type: 'error', text: 'The advertiser creative must be portrait.' });
    }
    revoke(creativeUrl);
    objectUrls.current.add(url);
    setCreativeFile(file);
    setCreativeUrl(url);
    setNotice(image.naturalWidth >= 2160 && image.naturalHeight >= 3840 ? null : { type: 'info', text: 'This image will work, but 2160 × 3840 is recommended for sharp zoomed-in results on large venue photos.' });
    results.forEach((result) => revoke(result.previewUrl));
    setResults([]);
  }

  async function generate() {
    if (!creativeFile || !selected.size) return;
    setProgress({ current: 0, total: selected.size });
    setNotice(null);
    results.forEach((result) => revoke(result.previewUrl));
    const next: GeneratedResult[] = [];
    try {
      if (!creativeUrl) throw new Error('Choose a creative first.');
      const source = await loadImage(creativeUrl);
      const creative = prepareCreative(source, source.naturalWidth, source.naturalHeight);
      const photos = mappedPhotos.filter((photo) => selected.has(photo.id));
      const enlargedScenes = photos.filter((photo) => photo.screens.some((screen) => screenExceedsCreative(screen, creative))).length;
      for (let index = 0; index < photos.length; index += 1) {
        const photo = photos[index];
        const venue = await loadImage(photoUrl(photo));
        const canvas = await renderMockup(venue, creative, photo.screens);
        const preview = await canvasToBlob(canvas, 'image/jpeg', 0.84);
        const previewUrl = URL.createObjectURL(preview);
        objectUrls.current.add(previewUrl);
        next.push({ photo, canvas, creative, previewUrl });
        setResults([...next]);
        setProgress({ current: index + 1, total: photos.length });
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      }
      setNotice(enlargedScenes
        ? { type: 'info', text: `${next.length} mockup${next.length === 1 ? '' : 's'} generated. ${enlargedScenes} venue${enlargedScenes === 1 ? '' : 's'} enlarged the creative; use a 2160 × 3840 source for the sharpest zoomed-in result.` }
        : { type: 'success', text: `${next.length} mockup${next.length === 1 ? '' : 's'} generated at full creative resolution.` });
    } catch (error) {
      setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Mockup generation failed.' });
    } finally {
      setProgress(null);
    }
  }

  async function download(result: GeneratedResult) {
    setDownloadingId(result.photo.id);
    setNotice({ type: 'info', text: `Preparing a presentation-ready download for ${result.photo.name}…` });
    try {
      const venue = await loadImage(photoUrl(result.photo));
      const longEdge = recommendedExportLongEdge(venue.naturalWidth, venue.naturalHeight, result.creative, result.photo.screens);
      const canvas = longEdge > Math.max(result.canvas.width, result.canvas.height)
        ? await renderMockup(venue, result.creative, result.photo.screens, longEdge, true)
        : result.canvas;
      downloadBlob(await canvasToPresentationJpeg(canvas), `mockup-${result.photo.id}.jpg`);
      if (canvas !== result.canvas) {
        canvas.width = 1;
        canvas.height = 1;
      }
      setNotice({ type: 'success', text: `${result.photo.name} download has started.` });
    } catch (error) {
      setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Download failed.' });
    } finally {
      setDownloadingId(null);
    }
  }

  async function downloadZip() {
    setZipBusy(true);
    const zip = new JSZip();
    try {
      for (let index = 0; index < results.length; index += 1) {
        const result = results[index];
        setNotice({ type: 'info', text: `Optimizing presentation images (${index + 1} of ${results.length})…` });
        const venue = await loadImage(photoUrl(result.photo));
        const longEdge = recommendedExportLongEdge(venue.naturalWidth, venue.naturalHeight, result.creative, result.photo.screens);
        const canvas = longEdge > Math.max(result.canvas.width, result.canvas.height)
          ? await renderMockup(venue, result.creative, result.photo.screens, longEdge, true)
          : result.canvas;
        zip.file(`mockup-${result.photo.id}.jpg`, await canvasToPresentationJpeg(canvas));
        if (canvas !== result.canvas) {
          canvas.width = 1;
          canvas.height = 1;
        }
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      }
      downloadBlob(await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } }), 'playdooh-mockups.zip');
      setNotice({ type: 'success', text: 'The presentation-ready ZIP download has started.' });
    } catch (error) {
      setNotice({ type: 'error', text: error instanceof Error ? error.message : 'ZIP download failed.' });
    } finally {
      setZipBusy(false);
    }
  }

  async function updateMappings(photo: LibraryPhoto, screens: ScreenMapping[]) {
    const next = {
      ...manifest,
      updatedAt: new Date().toISOString(),
      photos: manifest.photos.map((item) => item.id === photo.id ? { ...item, screens } : item),
    };
    try {
      const saved = await saveManifest(next);
      setManifest(saved);
      setMappingPhoto(null);
      setNotice({ type: 'success', text: `Mappings saved for ${photo.name}.` });
    } catch (error) {
      setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not save mappings.' });
      throw error;
    }
  }

  async function removePhoto(photo: LibraryPhoto) {
    if (!window.confirm(`Delete ${photo.name} and its mappings? This cannot be undone.`)) return;
    try {
      await deletePhoto(photo.id);
      setManifest((current) => ({ ...current, photos: current.photos.filter((item) => item.id !== photo.id) }));
      setNotice({ type: 'success', text: `${photo.name} was deleted.` });
    } catch (error) {
      setNotice({ type: 'error', text: error instanceof Error ? error.message : 'Could not delete photo.' });
    }
  }

  if (mappingPhoto) return <MappingEditor photo={mappingPhoto} onClose={() => setMappingPhoto(null)} onSave={(screens) => updateMappings(mappingPhoto, screens)} />;

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="PlayDooh home"><img src="/assets/playdooh-logo.png" alt="PlayDooh" /></a>
        <nav className="mode-switch" aria-label="Tool mode">
          <button className={mode === 'sales' ? 'active' : ''} onClick={() => setMode('sales')}><WandSparkles size={17} /> Sales generation</button>
          <button className={mode === 'admin' ? 'active' : ''} onClick={() => setMode('admin')}><Map size={17} /> Admin mapping</button>
        </nav>
      </header>
      <main id="top">
        <section className="hero compact-hero">
          <h1>{mode === 'sales' ? 'Build venue mockups in minutes.' : 'Map the screen once. Reuse it every time.'}</h1>
          <div className="hero-stats">
            <span><strong>{libraryPhotos.length}</strong> venue scenes</span>
            <span><strong>{libraryPhotos.reduce((sum, photo) => sum + photo.screens.length, 0)}</strong> mapped screens</span>
          </div>
        </section>

        {notice && <div className={`notice ${notice.type}`} role="status"><span>{notice.text}</span><button onClick={() => setNotice(null)} aria-label="Dismiss"><X size={16} /></button></div>}

        {mode === 'sales' ? (
          <>
            <section className="workspace-grid">
              <div className="panel sticky-panel">
                <div className="section-heading"><span className="step-number">1</span><div><h2>Upload creative</h2><p>Portrait image. 2160 × 3840 recommended for high-resolution venues.</p></div></div>
                <input ref={creativeInput} hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => pickCreative(event.target.files?.[0])} />
                {creativeUrl ? (
                  <div className="creative-preview">
                    <img src={creativeUrl} alt="Uploaded advertiser creative" />
                    <div><strong>{creativeFile?.name}</strong><button className="text-button" onClick={() => creativeInput.current?.click()}>Replace</button></div>
                  </div>
                ) : (
                  <button className="upload-drop" onClick={() => creativeInput.current?.click()}><Upload size={24} /><strong>Choose image</strong><span>Portrait JPG, PNG, or WebP.</span></button>
                )}
                <button className="btn primary wide generate-button" onClick={generate} disabled={!creativeFile || !selected.size || !!progress}>
                  {progress ? <><LoaderCircle className="spin" size={18} /> Generating {progress.current} of {progress.total}</> : <><WandSparkles size={18} /> Generate {selected.size ? `${selected.size} mockup${selected.size === 1 ? '' : 's'}` : 'mockups'}</>}
                </button>
                {progress && <div className="progress"><span style={{ width: `${(progress.current / progress.total) * 100}%` }} /></div>}
              </div>
              <div className="panel">
                <div className="section-heading venue-heading"><span className="step-number">2</span><div><h2>Select venue scenes</h2><p>One creative will be placed into every mapped screen.</p></div><div className="venue-tools"><button className="venue-select-all" type="button" onClick={toggleVisiblePhotos} disabled={!filteredPhotos.length}><CheckCheck size={15} /> {allVisibleSelected ? 'Clear all' : 'Select all'}</button><label className="venue-search"><Search size={15} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search venue or file" aria-label="Search venue or file name" />{search && <button type="button" onClick={() => setSearch('')} aria-label="Clear search"><X size={14} /></button>}</label></div></div>
                {loading ? <Loading /> : mappedPhotos.length ? filteredPhotos.length ? (
                  <div className="photo-grid">
                    {filteredPhotos.map((photo) => <PhotoCard key={photo.id} photo={photo} selectable selected={selected.has(photo.id)} onSelect={() => setSelected((current) => { const next = new Set(current); next.has(photo.id) ? next.delete(photo.id) : next.add(photo.id); return next; })} />)}
                  </div>
                ) : <Empty title="No matching venues" copy="Try a different venue or file name." /> : <Empty title="No mapped venues yet" copy="Ask an admin to upload a venue photo and map its screens." />}
              </div>
            </section>
            {results.length > 0 && (
              <section className="panel results-panel">
                <div className="results-heading"><div><span className="eyebrow">Ready to share</span><h2>Generated mockups</h2><p>Presentation-ready downloads are optimized to 1 MB or less each.</p></div><button className="btn secondary" onClick={downloadZip} disabled={zipBusy || !!downloadingId}>{zipBusy ? <><LoaderCircle className="spin" size={17} /> Preparing ZIP…</> : <><Layers3 size={17} /> Download all ZIP</>}</button></div>
                <div className="results-grid">{results.map((result, index) => (
                  <article className="result-card" key={result.photo.id}><button className="result-preview" onClick={() => setGalleryIndex(index)} aria-label={`View ${result.photo.name} in gallery`}><img src={result.previewUrl} alt={`Generated mockup for ${result.photo.name}`} /><span><Maximize2 size={16} /> View</span></button><div><strong>{result.photo.name}</strong><span>{result.canvas.width} × {result.canvas.height}</span></div><div className="result-actions single"><button onClick={() => download(result)} disabled={downloadingId === result.photo.id}>{downloadingId === result.photo.id ? <><LoaderCircle className="spin" size={15} /> Preparing…</> : <><Download size={15} /> Download</>}</button></div></article>
                ))}</div>
              </section>
            )}
          </>
        ) : (
          <section className="panel admin-panel">
            <div className="results-heading"><div><span className="eyebrow">Shared R2 library</span><h2>Venue photo library</h2><p>Map each screen once, then reuse the venue photo.</p></div><button className="btn primary" onClick={() => setUploadOpen(true)}><Plus size={18} /> Add venue photos</button></div>
            {loading ? <Loading /> : libraryPhotos.length ? <div className="admin-photo-grid">{libraryPhotos.map((photo) => (
              <div className="admin-photo" key={photo.id}><PhotoCard photo={photo} onOpen={() => setMappingPhoto(photo)} /><div className="admin-card-actions"><button onClick={() => setMappingPhoto(photo)}><Map size={15} /> Map screens</button><button className="delete" onClick={() => removePhoto(photo)} aria-label={`Delete ${photo.name}`}><Trash2 size={15} /></button></div></div>
            ))}</div> : <Empty title="Your library is empty" copy="Upload a venue photo to begin mapping screens." />}
          </section>
        )}
      </main>
      <footer><span>PlayDooh Sales Mockup Tool</span></footer>
      {uploadOpen && <UploadModal onClose={() => setUploadOpen(false)} onUploaded={(photos) => { setManifest((current) => ({ ...current, photos: [...current.photos, ...photos] })); setUploadOpen(false); setNotice({ type: 'success', text: `${photos.length} venue file${photos.length === 1 ? '' : 's'} uploaded. Unmapped items are marked in the library.` }); }} />}
      {galleryIndex !== null && results[galleryIndex] && <Gallery results={results} index={galleryIndex} onIndex={setGalleryIndex} onClose={() => setGalleryIndex(null)} />}
    </div>
  );
}

function Gallery({ results, index, onIndex, onClose }: { results: GeneratedResult[]; index: number; onIndex: (index: number) => void; onClose: () => void }) {
  const result = results[index];
  const previous = () => onIndex((index - 1 + results.length) % results.length);
  const next = () => onIndex((index + 1) % results.length);
  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft' && results.length > 1) previous();
      if (event.key === 'ArrowRight' && results.length > 1) next();
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  });
  return <div className="gallery-backdrop" role="dialog" aria-modal="true" aria-label="Generated mockup gallery" onClick={onClose}><div className="gallery" onClick={(event) => event.stopPropagation()}><div className="gallery-top"><div><strong>{result.photo.name}</strong><span>{index + 1} of {results.length}</span></div><button className="icon-button" onClick={onClose} aria-label="Close gallery"><X /></button></div><div className="gallery-media"><img src={result.previewUrl} alt={`Generated mockup for ${result.photo.name}`} /></div>{results.length > 1 && <><button className="gallery-nav previous" onClick={previous} aria-label="Previous mockup"><ChevronLeft /></button><button className="gallery-nav next" onClick={next} aria-label="Next mockup"><ChevronRight /></button></>}</div></div>;
}

function Loading() { return <div className="loading"><LoaderCircle className="spin" /><span>Loading library…</span></div>; }
function Empty({ title, copy }: { title: string; copy: string }) { return <div className="empty"><ImagePlus size={32} /><strong>{title}</strong><span>{copy}</span></div>; }

function UploadModal({ onClose, onUploaded }: { onClose: () => void; onUploaded: (photos: LibraryPhoto[]) => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  function addFiles(list: FileList | File[]) {
    const incoming = Array.from(list);
    const valid = incoming.filter((file) => ['image/jpeg', 'image/png', 'image/webp'].includes(file.type));
    setFiles((current) => [...current, ...valid].filter((file, index, all) => all.findIndex((item) => item.name === file.name && item.size === file.size) === index));
    if (valid.length !== incoming.length) setError('Only JPG, PNG, and WebP venue photos are supported.');
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!files.length) return;
    setBusy(true); setError('');
    try {
      const uploaded: LibraryPhoto[] = [];
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const fileName = file.name.replace(/\.[^.]+$/, '');
        const displayName = files.length === 1 && name.trim() ? name.trim() : fileName;
        const id = `${slugify(displayName) || 'venue'}-${crypto.randomUUID().slice(0, 6)}`;
        const { image, url } = await readImageFile(file);
        uploaded.push(await uploadPhoto(file, id, displayName, image.naturalWidth, image.naturalHeight));
        URL.revokeObjectURL(url);
        setUploadProgress(index + 1);
      }
      onUploaded(uploaded);
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Upload failed.'); setBusy(false); }
  }

  return <div className="modal-backdrop" role="presentation"><div className="modal" role="dialog" aria-modal="true" aria-labelledby="upload-title"><div className="modal-heading"><div><span className="eyebrow">Admin upload</span><h2 id="upload-title">Add venue photos</h2></div><button className="icon-button" onClick={onClose} aria-label="Close"><X /></button></div><form onSubmit={submit}>{files.length <= 1 && <label>Display name <small>(optional)</small><input value={name} onChange={(event) => setName(event.target.value)} placeholder="Uses the filename if left blank" /></label>}<label className="multi-upload-drop" onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); addFiles(event.dataTransfer.files); }}><Upload size={25} /><strong>Drop venue photos here</strong><span>JPG, PNG, or WebP</span><input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={(event) => event.target.files && addFiles(event.target.files)} /></label>{files.length > 0 && <div className="upload-file-list">{files.map((file) => <span key={`${file.name}-${file.size}`}><ImagePlus size={14} /> {file.name}<button type="button" onClick={() => setFiles((current) => current.filter((item) => item !== file))} aria-label={`Remove ${file.name}`}><X size={13} /></button></span>)}</div>}{error && <div className="field-error">{error}</div>}<div className="modal-actions"><button className="btn ghost" type="button" onClick={onClose}>Cancel</button><button className="btn primary" disabled={busy || !files.length}>{busy ? `Uploading ${Math.min(uploadProgress + 1, files.length)} of ${files.length}…` : `Upload ${files.length || ''} photo${files.length === 1 ? '' : 's'}`}</button></div></form></div></div>;
}

export default App;
